import React from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Space, Button, message } from 'antd';
import { ReloadOutlined, DownloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function colorForStatus(status) {
  if (status === 'pass') return 'green';
  if (status === 'fail') return 'red';
  return 'gold';
}

export default function Compliance({ user }) {
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState({ overall: { pct: 0, passed: 0, total: 0 }, domains: [] });
  const [checks, setChecks] = React.useState([]);

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true);
      const [summaryRes, checksRes] = await Promise.all([
        axios.get('/api/ops/compliance/summary', { headers: authHeader() }),
        axios.get('/api/ops/compliance/checks', { headers: authHeader() })
      ]);
      setSummary(summaryRes.data || { overall: { pct: 0, passed: 0, total: 0 }, domains: [] });
      setChecks(checksRes.data?.checks || []);
    } catch {
      message.error('No se pudo cargar cumplimiento');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const runNow = async () => {
    try {
      setLoading(true);
      await axios.post('/api/ops/compliance/run', {}, { headers: authHeader() });
      message.success('Evaluacion de cumplimiento ejecutada');
      await loadData();
    } catch (err) {
      message.error(err.response?.data?.error || 'No se pudo ejecutar evaluacion');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get('/api/ops/compliance/export.csv', {
        headers: authHeader(),
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'nc3_compliance_matrix.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('No se pudo exportar CSV');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'requirement_id', key: 'requirement_id', width: 110 },
    { title: 'Dominio', dataIndex: 'domain', key: 'domain', width: 120 },
    { title: 'Requisito', dataIndex: 'requirement', key: 'requirement' },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value) => <Tag color={colorForStatus(value)}>{String(value || 'pending').toUpperCase()}</Tag>
    },
    { title: 'Evidencia', dataIndex: 'evidence', key: 'evidence' }
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Cumplimiento NC3</h1>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Actualizar</Button>
        {user?.role === 'admin' && (
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={runNow} loading={loading}>Ejecutar Ahora</Button>
        )}
        <Button icon={<DownloadOutlined />} onClick={exportCsv}>Exportar CSV</Button>
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Cumplimiento Global (%)" value={summary?.overall?.pct || 0} precision={1} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Controles Aprobados" value={summary?.overall?.passed || 0} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Controles Totales" value={summary?.overall?.total || 0} /></Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {(summary?.domains || []).map((d) => (
          <Col xs={24} sm={12} lg={8} key={d.domain}>
            <Card size="small" title={d.domain}>
              <Statistic title="Cumplimiento" value={d.pct || 0} suffix="%" precision={1} />
              <div style={{ marginTop: 8 }}>{d.passed || 0}/{d.total || 0} controles</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Table
          rowKey="requirement_id"
          loading={loading}
          columns={columns}
          dataSource={checks}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
}
